import { contractService } from 'content/service';
import { ContractInterface } from 'contract/type';
import React, { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { message, Modal, Descriptions, Alert, List, Spin } from 'antd';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { BASE_URL, CONTRACT_EXPORT_API_PATH, CONTRACT_UPLOAD_API_PATH } from 'constants/api';
import axios from 'axios';
import * as Component from '../../../../shared/components';
import { getContractColumns } from './ContractColumns';
import { uploadHandlers } from './handlers';
import tableHandlers from './handlers/tableHandlers';

export default function Contract(): React.JSX.Element {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchContracts = async (
    params: URLSearchParams
  ): Promise<{ data: ContractInterface[]; count: number }> => {
    const contractList = await contractService.fetch(params);
    return { data: contractList.results, count: contractList.count };
  };

  const { handleOnClickDeleteButton, handleOnClickTitle, handleOnClickSetting, handleAddClick } =
    tableHandlers({
      navigate,
    });

  uploadHandlers();

  const columns = getContractColumns({
    handleOnClickTitle,
    handleOnClickSetting,
    handleOnClickDeleteButton,
    searchParams,
  });

  // CSV Import
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [dryRunResult, setDryRunResult] = useState<{
    contracts_count: number;
    terms_count: number;
    skipped_contracts_count: number;
    skipped_contracts: { contract_key?: string; error?: string }[];
    skipped_missing_contract_file_rows: string[];
  } | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // input 초기화 (같은 파일 재선택 가능)
    e.target.value = '';

    setSelectedFile(file);
    setImportLoading(true);
    setImportModalOpen(true);
    setDryRunResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('dry_run', 'true');

      const url = `${BASE_URL}${CONTRACT_UPLOAD_API_PATH}`;
      const response = await axios.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setDryRunResult(response.data);
    } catch (error: unknown) {
      const errorMsg =
        axios.isAxiosError(error) && error.response?.data?.error
          ? error.response.data.error
          : String(error);
      message.error(`CSV 미리보기 실패: ${errorMsg}`);
      setImportModalOpen(false);
    } finally {
      setImportLoading(false);
    }
  };

  const handleImportConfirm = async () => {
    if (!selectedFile) return;

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const url = `${BASE_URL}${CONTRACT_UPLOAD_API_PATH}`;
      const response = await axios.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const { contracts_count, terms_count } = response.data;
      message.success(`Import 완료: 계약 ${contracts_count}건, 약정 ${terms_count}건 생성`);
      setImportModalOpen(false);
      setSelectedFile(null);
      setDryRunResult(null);

      // 목록 새로고침
      setSearchParams(new URLSearchParams(searchParams));
    } catch (error: unknown) {
      const errorMsg =
        axios.isAxiosError(error) && error.response?.data?.error
          ? error.response.data.error
          : String(error);
      message.error(`Import 실패: ${errorMsg}`);
    } finally {
      setImporting(false);
    }
  };

  const handleImportCancel = () => {
    setImportModalOpen(false);
    setSelectedFile(null);
    setDryRunResult(null);
  };

  const handleExcelDownload = async (): Promise<void> => {
    try {
      message.loading('Downloading contracts...', 0);

      // 현재 searchParams를 사용하여 다운로드 API 호출
      const downloadParams = new URLSearchParams(searchParams);
      // 페이지네이션 관련 파라미터 제거 (전체 다운로드)
      downloadParams.delete('page');
      downloadParams.delete('page_size');

      const url = `${BASE_URL}${CONTRACT_EXPORT_API_PATH}`;

      // axios를 사용하여 파일 다운로드 (인증 정보 자동 포함)
      const response = await axios.get(url, {
        params: downloadParams,
        responseType: 'blob',
      });

      // response header에서 파일명 추출
      const contentDisposition = response.headers['content-disposition'];
      let filename = `contracts_${new Date().toISOString().split('T')[0]}.csv`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      // blob으로 다운로드
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // 메모리 정리
      window.URL.revokeObjectURL(downloadUrl);

      message.destroy();
      message.success('Contract download completed');
    } catch (error) {
      message.destroy();
      message.error(`Failed to download contracts: ${error}`);
    }
  };

  const ExcelDownloadButton = (): JSX.Element => {
    return (
      <Component.CustomButtonComponent
        text={`CSV Download`}
        icon={<DownloadOutlined />}
        onClick={handleExcelDownload}
        size={'small'}
        type="default"
      />
    );
  };

  const ImportButton = (): JSX.Element => {
    return (
      <>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
        <Component.CustomButtonComponent
          text="Import"
          icon={<UploadOutlined />}
          onClick={handleImportClick}
          size={'small'}
          type="default"
        />
      </>
    );
  };

  const AddButton = (): JSX.Element => {
    return (
      <Component.CustomButtonComponent
        text={`+ Add Contract`}
        onClick={handleAddClick}
        size={'small'}
      />
    );
  };

  return (
    <Component.BasePageComponent>
      <Component.TableComponent<ContractInterface>
        name="Contract"
        dataFetch={fetchContracts}
        columns={columns}
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        additionalButton={[ImportButton(), ExcelDownloadButton(), AddButton()]}
      />
      <Modal
        title="CSV Import 미리보기"
        open={importModalOpen}
        onOk={handleImportConfirm}
        onCancel={handleImportCancel}
        okText="Import 실행"
        cancelText="취소"
        okButtonProps={{ disabled: importLoading || !dryRunResult, loading: importing }}
        width={720}
      >
        {importLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
            <p style={{ marginTop: 16 }}>CSV 파일 분석 중...</p>
          </div>
        ) : (
          dryRunResult && (
            <>
              <Descriptions column={2} bordered size="small" style={{ marginBottom: 16 }}>
                <Descriptions.Item label="생성 예정 계약">
                  {dryRunResult.contracts_count}건
                </Descriptions.Item>
                <Descriptions.Item label="생성 예정 약정">
                  {dryRunResult.terms_count}건
                </Descriptions.Item>
                <Descriptions.Item label="스킵 계약" span={2}>
                  {dryRunResult.skipped_contracts_count}건
                </Descriptions.Item>
              </Descriptions>
              {dryRunResult.skipped_contracts_count > 0 && (
                <Alert
                  type="warning"
                  message={`${dryRunResult.skipped_contracts_count}건의 계약이 스킵됩니다`}
                  description={
                    <List
                      size="small"
                      dataSource={dryRunResult.skipped_contracts}
                      renderItem={item => (
                        <List.Item>
                          {item.contract_key || 'Unknown'}: {item.error || 'Unknown error'}
                        </List.Item>
                      )}
                      style={{ maxHeight: 200, overflow: 'auto' }}
                    />
                  }
                  style={{ marginBottom: 16 }}
                />
              )}
              {dryRunResult.skipped_missing_contract_file_rows.length > 0 && (
                <Alert
                  type="warning"
                  message="contract_file_link 누락 행"
                  description={
                    <div style={{ maxHeight: 100, overflow: 'auto' }}>
                      {dryRunResult.skipped_missing_contract_file_rows.join(', ')}
                    </div>
                  }
                />
              )}
            </>
          )
        )}
      </Modal>
    </Component.BasePageComponent>
  );
}
